/**
 * A focus trap for the modal lane — WCAG 2.1 AA: "focus trap, focus return, Escape to close"
 * (WP-18, PRD §1.5.5).
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab/Shift+Tab within a container and returns focus to a given element on release.
 *
 * @param {HTMLElement} container
 * @return {{activate: (returnTo?: HTMLElement|null) => void, release: () => void}}
 */
export function createFocusTrap( container ) {
	let previouslyFocused = null;

	/**
	 * @param {KeyboardEvent} event
	 * @return {void}
	 */
	function onKeydown( event ) {
		if ( 'Tab' !== event.key ) {
			return;
		}

		const focusable = Array.from( container.querySelectorAll( FOCUSABLE ) ).filter(
			( element ) => element.offsetParent !== null
		);

		if ( 0 === focusable.length ) {
			return;
		}

		const first = focusable[ 0 ];
		const last = focusable[ focusable.length - 1 ];

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	}

	return {
		/**
		 * @param {HTMLElement|null} returnTo The element focus goes back to on release. Pass it
		 *                                    whenever the caller knows it: Safari (macOS and iOS)
		 *                                    does not focus a button that is clicked, so
		 *                                    `document.activeElement` is `<body>` there and focus
		 *                                    would be lost on close — for a VoiceOver user, dropped
		 *                                    at the top of the page.
		 */
		activate( returnTo = null ) {
			previouslyFocused = returnTo || document.activeElement;
			container.addEventListener( 'keydown', onKeydown );

			const focusable = container.querySelector( FOCUSABLE );

			if ( focusable ) {
				focusable.focus();
			} else {
				container.setAttribute( 'tabindex', '-1' );
				container.focus();
			}
		},
		release() {
			container.removeEventListener( 'keydown', onKeydown );

			if ( previouslyFocused && 'function' === typeof previouslyFocused.focus ) {
				previouslyFocused.focus();
			}

			previouslyFocused = null;
		},
	};
}
